import { Fragment, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'

import type {
    DriverRemittance,
    DriverRemittanceStatus,
} from '@/lib/api/driver-cash-api'
import type { HeldDriverGroup } from '@/lib/driver-cash'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ReceiveRemittanceDialog } from '@/components/driver-cash/receive-remittance-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
    useDriverCashReport,
    useHeldPayments,
    useRemittances,
    useVoidRemittance,
} from '@/hooks/use-driver-cash-query'
import { todayYMD } from '@/lib/date-presets'
import {
    customerMethodLabel,
    groupHeldByDriver,
    heldDays,
    isBackdated,
    receiveMethodLabel,
} from '@/lib/driver-cash'
import { formatDateTimeShort, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/remittances',
)({
    component: RemittancesPage,
})

const ALL_DRIVERS = 'all'
const HISTORY_PAGE_SIZE = 50

function statusLabel(status: DriverRemittanceStatus): string {
    switch (status) {
        case 'pending':
            return m.driverRemittance_status_pending()
        case 'confirmed':
            return m.driverRemittance_status_confirmed()
        case 'rejected':
            return m.driverRemittance_status_rejected()
        case 'voided':
            return m.driverRemittance_status_voided()
    }
}

/**
 * Quỹ tiền tài xế thu hộ (M-133): tiền khách trả cho tài xế chỉ thành tiền HTX
 * khi kế toán bấm "Nhận tiền" và tick đúng các đơn tài xế đã đưa.
 */
function RemittancesPage() {
    const today = todayYMD()
    const held = useHeldPayments()
    // Only the server total is read; one row keeps the request small.
    const receivedToday = useRemittances({
        status: 'confirmed',
        date_from: today,
        date_to: today,
        per_page: 1,
    })
    const groups = useMemo(
        () => groupHeldByDriver(held.data ?? []),
        [held.data],
    )
    const heldTotal = groups.reduce((sum, g) => sum + g.total, 0)
    const todayTotal = receivedToday.data?.meta?.totals?.received_amount ?? 0

    return (
        <div className="space-y-4 page-pad">
            <div className="space-y-1">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.driverRemittance_review_title()}
                    </h1>
                </div>
                <p className="text-sm text-muted-foreground">
                    {m.driverRemittance_review_desc()}
                </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                <Kpi
                    label={m.driverRemittance_kpi_held()}
                    value={formatVnd(heldTotal)}
                    testId="kpi-held"
                    accent
                />
                <Kpi
                    label={m.driverRemittance_kpi_drivers()}
                    value={String(groups.length)}
                />
                <Kpi
                    label={m.driverRemittance_kpi_today()}
                    value={formatVnd(todayTotal)}
                />
            </div>

            <Tabs defaultValue="held" className="space-y-3">
                <TabsList>
                    <TabsTrigger value="held">
                        {m.driverRemittance_tab_held()}
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        {m.driverRemittance_tab_history()}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="held">
                    <HeldTab groups={groups} isLoading={held.isLoading} />
                </TabsContent>
                <TabsContent value="history">
                    <HistoryTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}

function Kpi({
    label,
    value,
    testId,
    accent = false,
}: {
    label: string
    value: string
    testId?: string
    accent?: boolean
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                    {label}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <p
                    className={cn(
                        'text-lg font-semibold tabular-nums',
                        accent && 'text-primary',
                    )}
                    data-testid={testId}
                >
                    {value}
                </p>
            </CardContent>
        </Card>
    )
}

function ExpandButton({
    open,
    onClick,
}: {
    open: boolean
    onClick: () => void
}) {
    return (
        <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-expanded={open}
            aria-label={m.driverRemittance_expand_orders()}
            onClick={onClick}
        >
            <ChevronRight
                className={cn(
                    'size-4 transition-transform',
                    open && 'rotate-90',
                )}
            />
        </Button>
    )
}

function HeldTab({
    groups,
    isLoading,
}: {
    groups: HeldDriverGroup[]
    isLoading: boolean
}) {
    const [expanded, setExpanded] = useState<string | null>(null)
    const [target, setTarget] = useState<HeldDriverGroup | null>(null)

    if (isLoading) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (groups.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.driverRemittance_held_empty()}
            </p>
        )
    }
    return (
        <Card>
            <CardContent className="space-y-2 pt-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10" />
                            <TableHead>
                                {m.driverRemittance_col_driver()}
                            </TableHead>
                            <TableHead>
                                {m.driverRemittance_col_orders()}
                            </TableHead>
                            <TableHead>
                                {m.driverRemittance_col_since()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.driverRemittance_col_held()}
                            </TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {groups.map((g) => {
                            const open = expanded === g.driverId
                            const days = heldDays(g.since)
                            return (
                                <Fragment key={g.driverId}>
                                    <TableRow>
                                        <TableCell>
                                            <ExpandButton
                                                open={open}
                                                onClick={() =>
                                                    setExpanded(
                                                        open
                                                            ? null
                                                            : g.driverId,
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {g.driverName || g.driverId}
                                        </TableCell>
                                        <TableCell>
                                            {g.payments.length}
                                        </TableCell>
                                        <TableCell
                                            className={cn(
                                                days >= 1 &&
                                                    'font-medium text-amber-600',
                                            )}
                                        >
                                            {days >= 1
                                                ? m.driverRemittance_since_days(
                                                      {
                                                          date: formatDateTimeShort(
                                                              g.since,
                                                          ),
                                                          days,
                                                      },
                                                  )
                                                : formatDateTimeShort(g.since)}
                                        </TableCell>
                                        <TableCell className="text-right font-semibold tabular-nums">
                                            {formatVnd(g.total)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                onClick={() => setTarget(g)}
                                            >
                                                {m.driverRemittance_receive()}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {open && (
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableCell />
                                            <TableCell colSpan={5}>
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>
                                                                {m.driverRemittance_col_order()}
                                                            </TableHead>
                                                            <TableHead>
                                                                {m.driverRemittance_col_customer()}
                                                            </TableHead>
                                                            <TableHead>
                                                                {m.driverRemittance_col_customer_method()}
                                                            </TableHead>
                                                            <TableHead>
                                                                {m.driverRemittance_col_collected_at()}
                                                            </TableHead>
                                                            <TableHead className="text-right">
                                                                {m.driverRemittance_amount()}
                                                            </TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {g.payments.map((p) => (
                                                            <TableRow
                                                                key={
                                                                    p.payment_id
                                                                }
                                                            >
                                                                <TableCell>
                                                                    {p.order_code ||
                                                                        '—'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {
                                                                        p.customer_name
                                                                    }
                                                                </TableCell>
                                                                <TableCell>
                                                                    {customerMethodLabel(
                                                                        p.payment_method,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {formatDateTimeShort(
                                                                        p.collected_at,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-right tabular-nums">
                                                                    {formatVnd(
                                                                        p.amount,
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            )
                        })}
                    </TableBody>
                </Table>
                <p className="text-xs text-muted-foreground">
                    {m.driverRemittance_held_note()}
                </p>
            </CardContent>
            <ReceiveRemittanceDialog
                group={target}
                onClose={() => setTarget(null)}
            />
        </Card>
    )
}

/** First day of the current VN month, YYYY-MM-DD. */
function monthStart(today: string): string {
    return `${today.slice(0, 8)}01`
}

function HistoryTab() {
    const today = todayYMD()
    const [from, setFrom] = useState(() => monthStart(today))
    const [to, setTo] = useState(today)
    const [driverId, setDriverId] = useState(ALL_DRIVERS)
    const [page, setPage] = useState(1)
    const { data: drivers } = useDriverCashReport()
    const { data, isLoading } = useRemittances({
        date_from: from || undefined,
        date_to: to || undefined,
        driver_id: driverId === ALL_DRIVERS ? undefined : driverId,
        page,
        per_page: HISTORY_PAGE_SIZE,
    })
    const totalPages = data?.meta?.total_pages ?? 1
    const [expanded, setExpanded] = useState<string | null>(null)
    const [voidTarget, setVoidTarget] = useState<DriverRemittance | null>(null)
    const rows = data?.data ?? []

    return (
        <Card>
            <CardContent className="space-y-3 pt-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <Label htmlFor="remittance-from">
                            {m.driverRemittance_filter_from()}
                        </Label>
                        <Input
                            id="remittance-from"
                            type="date"
                            value={from}
                            onChange={(e) => {
                                setPage(1)
                                setFrom(e.target.value)
                            }}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="remittance-to">
                            {m.driverRemittance_filter_to()}
                        </Label>
                        <Input
                            id="remittance-to"
                            type="date"
                            value={to}
                            onChange={(e) => {
                                setPage(1)
                                setTo(e.target.value)
                            }}
                        />
                    </div>
                    <div className="w-56">
                        <Select
                            value={driverId}
                            onValueChange={(v) => {
                                setPage(1)
                                setDriverId(v)
                            }}
                        >
                            <SelectTrigger
                                aria-label={m.driverRemittance_col_driver()}
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL_DRIVERS}>
                                    {m.driverRemittance_filter_driver_all()}
                                </SelectItem>
                                {(drivers ?? []).map((d) => (
                                    <SelectItem
                                        key={d.driver_id}
                                        value={d.driver_id}
                                    >
                                        {d.driver_name || d.driver_id}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {isLoading ? (
                    <p className="text-sm text-muted-foreground">
                        {m.common_loading()}
                    </p>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {m.driverRemittance_history_empty()}
                    </p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10" />
                                <TableHead>
                                    {m.driverRemittance_col_received_at()}
                                </TableHead>
                                <TableHead>
                                    {m.driverRemittance_col_driver()}
                                </TableHead>
                                <TableHead>
                                    {m.driverRemittance_col_orders()}
                                </TableHead>
                                <TableHead>
                                    {m.driverRemittance_col_receive_method()}
                                </TableHead>
                                <TableHead>
                                    {m.driverRemittance_col_receiver()}
                                </TableHead>
                                <TableHead className="text-right">
                                    {m.driverRemittance_amount()}
                                </TableHead>
                                <TableHead>
                                    {m.driverRemittance_col_status()}
                                </TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => {
                                const open = expanded === r.id
                                const payments = r.payments ?? []
                                return (
                                    <Fragment key={r.id}>
                                        <TableRow>
                                            <TableCell>
                                                {payments.length > 0 && (
                                                    <ExpandButton
                                                        open={open}
                                                        onClick={() =>
                                                            setExpanded(
                                                                open
                                                                    ? null
                                                                    : r.id,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTimeShort(
                                                    r.confirmed_at ??
                                                        r.created_at,
                                                )}
                                                {isBackdated(r) && (
                                                    <p className="text-xs text-muted-foreground">
                                                        {m.driverRemittance_recorded_at(
                                                            {
                                                                time: formatDateTimeShort(
                                                                    r.created_at,
                                                                ),
                                                            },
                                                        )}
                                                    </p>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.driver_name || r.driver_id}
                                            </TableCell>
                                            <TableCell>
                                                {payments.length || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {receiveMethodLabel(
                                                    r.receive_method,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.confirmed_by_name || '—'}
                                            </TableCell>
                                            <TableCell className="text-right font-semibold tabular-nums">
                                                {formatVnd(
                                                    r.confirmed_amount ??
                                                        r.amount,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        r.status === 'confirmed'
                                                            ? 'default'
                                                            : 'outline'
                                                    }
                                                >
                                                    {statusLabel(r.status)}
                                                </Badge>
                                                {r.status === 'voided' &&
                                                    r.note && (
                                                        <p className="mt-1 text-xs text-muted-foreground">
                                                            {m.driverRemittance_void_reason(
                                                                {
                                                                    reason: r.note,
                                                                },
                                                            )}
                                                        </p>
                                                    )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {r.status === 'confirmed' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            setVoidTarget(r)
                                                        }
                                                    >
                                                        {m.driverRemittance_void()}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {open && (
                                            <TableRow className="bg-muted/40 hover:bg-muted/40">
                                                <TableCell />
                                                <TableCell colSpan={8}>
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead>
                                                                    {m.driverRemittance_col_order()}
                                                                </TableHead>
                                                                <TableHead>
                                                                    {m.driverRemittance_col_customer()}
                                                                </TableHead>
                                                                <TableHead>
                                                                    {m.driverRemittance_col_customer_method()}
                                                                </TableHead>
                                                                <TableHead className="text-right">
                                                                    {m.driverRemittance_amount()}
                                                                </TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {payments.map(
                                                                (p) => (
                                                                    <TableRow
                                                                        key={
                                                                            p.payment_id
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            {p.order_code ||
                                                                                '—'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                p.customer_name
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {customerMethodLabel(
                                                                                p.payment_method,
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell className="text-right tabular-nums">
                                                                            {formatVnd(
                                                                                p.amount,
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ),
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
                {totalPages > 1 && (
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_prev()}
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            {page} / {totalPages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_next()}
                        </Button>
                    </div>
                )}
            </CardContent>
            <VoidDialog
                target={voidTarget}
                onClose={() => setVoidTarget(null)}
            />
        </Card>
    )
}

/** Huỷ phiếu: lý do bắt buộc; các đơn của phiếu quay lại "Đang giữ". */
function VoidDialog({
    target,
    onClose,
}: {
    target: DriverRemittance | null
    onClose: () => void
}) {
    const voidRemittance = useVoidRemittance()
    const [reason, setReason] = useState('')
    const [showError, setShowError] = useState(false)
    const workflow = useWorkflowTracking('driver_remittance', {
        active: target !== null,
        attemptKey: target?.id,
        operation: 'void',
    })

    const close = () => {
        setReason('')
        setShowError(false)
        onClose()
    }

    const submit = () => {
        if (!target) return
        const note = reason.trim()
        if (!note) {
            setShowError(true)
            workflow.step('validation_failed')
            return
        }
        void workflow
            .run(
                () => voidRemittance.mutateAsync({ id: target.id, note }),
                (res) => res.data?.id,
            )
            .then(close)
            .catch(() => {
                /* Mutation hook owns the error toast. */
            })
    }

    return (
        <Dialog open={target !== null} onOpenChange={(o) => !o && close()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.driverRemittance_void_title()}</DialogTitle>
                </DialogHeader>
                <div className="space-y-1">
                    <Label htmlFor="remittance-reason">
                        {m.driverRemittance_reason_label()}
                    </Label>
                    <Input
                        id="remittance-reason"
                        value={reason}
                        aria-invalid={showError}
                        onChange={(e) => {
                            workflow.interact('details')
                            setShowError(false)
                            setReason(e.target.value)
                        }}
                        placeholder={m.driverRemittance_reason_placeholder()}
                    />
                    {showError && (
                        <p className="text-sm text-destructive" role="alert">
                            {m.driverRemittance_void_required()}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="destructive"
                        onClick={submit}
                        disabled={voidRemittance.isPending}
                    >
                        {m.driverRemittance_void()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
